import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Fieldset } from 'primereact/fieldset';
import { useEffect, useMemo, useState } from 'react';

const PrioritiesSidebar = ({
    visible,
    onHide,
    prioColumn,
    priorities,
    prioEdit,
    updatePrioValue,
    onSave,
    rows,
    availableColumns = [],
    isSaving = false,
}) => {
    const [applySameTo, setApplySameTo] = useState([]);
    const [columnsCollapsed, setColumnsCollapsed] = useState(true);

    useEffect(() => {
        // Reset selection and collapse when opening/changing column
        if (visible) {
            setApplySameTo([]);
            setColumnsCollapsed(true);
        }
    }, [visible, prioColumn]);

    const targetColumns = useMemo(
        () => (availableColumns || []).filter(c => c !== prioColumn),
        [availableColumns, prioColumn]
    );

    const toggleApplyCol = (col, checked) => {
        setApplySameTo(prev => {
            const set = new Set(prev);
            if (checked) set.add(col); else set.delete(col);
            return Array.from(set);
        });
    };

    const getSampleForSource = (source) => {
        if (!rows || !Array.isArray(rows) || !prioColumn) return '';
        for (const r of rows) {
            if (r?.FromSource === source) {
                const v = r?.[prioColumn];
                if (v !== '' && v !== null && v !== undefined) {
                    const val = String(v);
                    return val.startsWith(':') ? val.slice(1) : val;
                }
            }
        }
        return '';
    };

    const promote = (src) => {
        const curr = Number(prioEdit[src] ?? 1);
        // If already 1, keep it at 1 and demote all other 1's to 2
        if (curr === 1) {
            Object.keys(prioEdit).forEach(k => {
                if (k !== src && Number(prioEdit[k] ?? 0) === 1) updatePrioValue(k, '2');
            });
            updatePrioValue(src, '1');
            return;
        }
        const next = Math.max(1, curr - 1);
        // If moving to 1, ensure uniqueness of 1 by pushing other 1's to 2
        if (next === 1) {
            Object.keys(prioEdit).forEach(k => {
                if (k !== src && Number(prioEdit[k] ?? 0) === 1) updatePrioValue(k, '2');
            });
        }
        updatePrioValue(src, String(next));
    };

    const demote = (src) => {
        const curr = Number(prioEdit[src] ?? 1);
        if (curr >= 10) return; // hide down button in UI when 10
        updatePrioValue(src, String(curr + 1));
    };

    // Keep display order by value, then by name
    const sortedSources = Object.keys(prioEdit).sort((a, b) => {
        const av = Number(prioEdit[a] ?? 999);
        const bv = Number(prioEdit[b] ?? 999);
        if (av !== bv) return av - bv;
        return a.localeCompare(b);
    });

    return (
        <Sidebar
            visible={visible}
            position="right"
            onHide={onHide}
            header={prioColumn ? `Edit Source Priorities for column "${prioColumn}"` : ''}
            style={{ width: '50rem' }}
        >
            {prioColumn ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {Object.keys(priorities?.[prioColumn] || prioEdit || {}).length === 0 && (
                        <div style={{ color: '#666' }}>
                            No priorities yet for this column.
                        </div>
                    )}

                    {Object.keys(prioEdit).length > 0 && (
                        <>
                            {/* Header row */}
                            <div style={{ display: 'flex', gap: 8, fontWeight: 600, color: '#444' }}>
                                <div style={{ width: 140 }}>Source</div>
                                <div style={{ width: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    Priority (1-10)
                                </div>
                                <div style={{ flex: 1 }}>Sample value</div>
                            </div>

                            {/* Priority rows */}
                            {sortedSources.map(src => {
                                const sample = getSampleForSource(src);
                                const valNum = Number(prioEdit[src] ?? 1);
                                return (
                                    <div key={src} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <label style={{ width: 140 }}>{src}</label>
                                        <div
                                            style={{
                                                width: 220,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 8
                                            }}
                                        >
                                            <Button
                                                icon="pi pi-arrow-up"
                                                className="p-button-sm p-button-rounded p-button-text"
                                                onClick={() => promote(src)}
                                                aria-label={`Promote priority for ${src}`}
                                                disabled={isSaving}
                                            />
                                            {valNum < 10 ? (
                                                <Button
                                                    icon="pi pi-arrow-down"
                                                    className="p-button-sm p-button-rounded p-button-text"
                                                    onClick={() => demote(src)}
                                                    aria-label={`Demote priority for ${src}`}
                                                    disabled={isSaving}
                                                />
                                            ) : (
                                                <Button
                                                    icon="pi pi-arrow-down"
                                                    className="p-button-sm p-button-rounded p-button-text"
                                                    style={{ visibility: 'hidden' }}
                                                    aria-hidden="true"
                                                    tabIndex={-1}
                                                />
                                            )}
                                            <span
                                                style={{
                                                    width: 44,
                                                    textAlign: 'center',
                                                    fontVariantNumeric: 'tabular-nums',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {valNum}
                                            </span>
                                            {valNum < 10 ? (
                                                <Button
                                                    icon="pi pi-times"
                                                    className="p-button-danger p-button-rounded p-button-text p-button-sm"
                                                    onClick={() => updatePrioValue(src, '10')}
                                                    aria-label={`Set ${src} priority to 10`}
                                                    disabled={isSaving}
                                                />
                                            ) : (
                                                <Button
                                                    icon="pi pi-times"
                                                    className="p-button-danger p-button-rounded p-button-text p-button-sm"
                                                    style={{ visibility: 'hidden' }}
                                                    aria-hidden="true"
                                                    tabIndex={-1}
                                                />
                                            )}
                                        </div>
                                        <span
                                            style={{
                                                flex: 1,
                                                color: '#555',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                            title={sample}
                                        >
                                            {sample || '—'}
                                        </span>
                                    </div>
                                );
                            })}
                        </>
                    )}

                    {/* Collapsible checklist AFTER priority selection */}
                    {targetColumns.length > 0 && (
                        <Fieldset
                            toggleable
                            collapsed={columnsCollapsed}
                            onToggle={(e) => setColumnsCollapsed(e.value)}
                            legend="Apply the same for"
                            className="p-mt-2"
                        >
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                    gap: 8
                                }}
                            >
                                {targetColumns.map(col => (
                                    <label key={col} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Checkbox
                                            inputId={`apply-${col}`}
                                            onChange={(e) => toggleApplyCol(col, e.checked)}
                                            checked={applySameTo.includes(col)}
                                            disabled={isSaving}
                                        />
                                        <span>{col}</span>
                                    </label>
                                ))}
                            </div>
                        </Fieldset>
                    )}

                    <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                        <Button
                            label={isSaving ? 'Applying…' : 'Apply'}
                            className="p-button-primary p-button-sm"
                            onClick={() => onSave(Array.from(new Set(applySameTo)))}
                            disabled={isSaving}
                        />
                        <Button
                            label="Cancel"
                            className="p-button-secondary p-button-sm"
                            onClick={onHide}
                            disabled={isSaving}
                        />
                    </div>
                </div>
            ) : null}
        </Sidebar>
    );
};

export default PrioritiesSidebar;